import { useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { formatCurrency } from "@covertree/utils";
import { Button, MeatballMenu } from "@covertree/ui";

import type {
  ExtraCoverageItem,
  ExtraCoverageItemField,
  FieldGroup,
} from "./extraCoverages";
import { ExtraCoverageItemForm } from "./ExtraCoverageItemForm";

const formatItemField = (
  item: ExtraCoverageItem,
  { name, type, options }: ExtraCoverageItemField
) => {
  if (type === "currency") return formatCurrency(Number(item[name]));

  const option = options?.find(({ value }) => value === item[name]);

  return `${option?.label ?? item[name]}`;
};

interface ItemsProps {
  fieldGroup: FieldGroup;
  items: ExtraCoverageItem[];
  isCustomizable: boolean;
  error?: string;
  onChange: (items: ExtraCoverageItem[]) => void;
  onDisable: () => void;
}

/**
 * Sub-item list for coverages such as Mine Subsidence Other Structures. Items
 * are addressed by index — the list is local, short, and never reordered.
 * `editing` also enforces that only one inline form is open per coverage.
 * An enabled coverage with no items is meaningless, so the add form is always
 * open then, and cancelling it turns the coverage off.
 */
export function ExtraCoverageItems({
  fieldGroup: { fields, validation, locatorKey },
  items,
  isCustomizable,
  error,
  onChange,
  onDisable,
}: Readonly<ItemsProps>) {
  const [editing, setEditing] = useState<number | "new" | null>(null);
  const hasItems = items.length > 0;

  const stopEditing = () => setEditing(null);

  const save = (item: ExtraCoverageItem) => {
    onChange(
      editing === "new" || !hasItems
        ? [...items, item]
        : items.map((existing, index) => (index === editing ? item : existing))
    );
    stopEditing();
  };

  const formatItem = (
    item: ExtraCoverageItem,
    fields: readonly ExtraCoverageItemField[]
  ) => fields.map((field) => formatItemField(item, field)).join(" · ");

  return (
    <Stack width={1} gap={1}>
      {items.map((item, index) => {
        const label = formatItem(item, fields);
        const key = item[locatorKey] ?? label + index;

        return isCustomizable && editing === index ? (
          <ExtraCoverageItemForm
            key={key}
            fields={fields}
            validation={validation}
            item={item}
            onSave={save}
            onCancel={stopEditing}
          />
        ) : (
          <Stack
            key={key}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            px={2}
            py={1}
            mb={1}
            border="1px solid"
            borderColor="action.disabled"
            borderRadius={2}>
            <Typography variant="body2">{label}</Typography>
            {isCustomizable && (
              <MeatballMenu
                center
                closeOnClick
                wrapperProps={{
                  sx: { "& .MuiIconButton-root": { border: "none" } },
                }}
                actionMenuItems={[
                  { label: "Edit", onClick: () => setEditing(index) },
                  {
                    label: "Remove",
                    onClick: () => {
                      stopEditing();
                      onChange(items.filter((_, i) => i !== index));
                    },
                  },
                ]}
              />
            )}
          </Stack>
        );
      })}

      {isCustomizable && (editing === "new" || !hasItems) ? (
        <ExtraCoverageItemForm
          fields={fields}
          validation={validation}
          showErrors={!!error}
          onSave={save}
          onCancel={hasItems ? stopEditing : onDisable}
        />
      ) : (
        isCustomizable &&
        editing === null && (
          <Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setEditing("new")}>
              Add item
            </Button>
          </Box>
        )
      )}

      {error && !hasItems && (
        <Typography variant="body2" color="error" role="alert">
          {error}
        </Typography>
      )}
    </Stack>
  );
}
